import { useAuthStore } from 'src/store/api/useAuthStore';
import { loginSaffAPI } from './api/API';
import { clubLoginMyClub } from './api/API';
import { PermissionsUser } from 'src/pages/LoginSaff';
import i18n from 'src/i18n';

// Unified response types that can handle both login types
export interface UnifiedLoginResponse {
  success: boolean;
  token: string;
  name_en?: string;
  name_ar?: string;
  user_id?: number; // user_id is now available for both login types
  permissions: PermissionsUser;
  data?: {
    user_id: number;
    club_id: string;
    user_id_saff: string | null;
    id_system: string;
    club_name_en: string;
    club_name_ar: string;
    person_name_en: string;
    person_name_ar: string;
    full_name_en: string;
    full_name_ar: string;
    email: string;
    username: string;
    phone: string;
    mobile: string;
    avatar: string;
    is_club: number;
    is_login: number;
    storage_limit: number;
    consumed_storage: number;
    trash_storage: number;
    upload_count: number;
    creator_id: string | null;
    role_id: string | null;
    created_at: string;
    updated_at: string;
  };
}

/**
 * Handles SAFF login (LoginMy endpoint)
 * @param code - The authentication code from URL
 * @returns Promise<UnifiedLoginResponse>
 */
export const handleSaffLogin = async (code: string): Promise<UnifiedLoginResponse> => {
  try {
    const response = await loginSaffAPI(code);
    
    // Transform SAFF response to unified format
    const unifiedResponse: UnifiedLoginResponse = {
      success: response.success || false,
      token: response.token || '',
      name_en: response.name_en,
      name_ar: response.name_ar,
      user_id: (response as any).user_id, // Include user_id from SAFF response (should be number like 162)
      permissions: {
        is_saff: response.permissions?.is_saff || false,
        create_role: response.permissions?.create_role,
        delete_role: response.permissions?.delete_role,
        edit_role: response.permissions?.edit_role,
        read_role: response.permissions?.read_role,
        users_saff_read: response.permissions?.users_saff_read,
        users_saff_edit_role: response.permissions?.users_saff_edit_role,
        create_users_outside_saff: response.permissions?.create_users_outside_saff,
        read_users_outside_saff: response.permissions?.read_users_outside_saff,
        edit_users_outside_saff: response.permissions?.edit_users_outside_saff,
        delete_users_outside_saff: response.permissions?.delete_users_outside_saff,
        share_delete_my_users_with_another: response.permissions?.share_delete_my_users_with_another,
        share_read_my_users_with_another: response.permissions?.share_read_my_users_with_another,
        share_edit_my_users_with_another: response.permissions?.share_edit_my_users_with_another,
        share_create_my_users_with_another: response.permissions?.share_create_my_users_with_another,
        can_share_with_club: response.permissions?.can_share_with_club,
        is_supper_admin: response.permissions?.is_supper_admin,
        is_administrators: response.permissions?.is_administrators,
      },
      // SAFF login doesn't have data object, so it's undefined
      data: undefined
    };
    
    return unifiedResponse;
  } catch (error: any) {
    throw new Error(`SAFF login failed: ${error.message}`);
  }
};

/**
 * Handles Club login (LoginClub endpoint)
 * @param code - The authentication code from URL
 * @returns Promise<UnifiedLoginResponse>
 */
export const handleClubLogin = async (code: string): Promise<UnifiedLoginResponse> => {
  try {
    const response = await clubLoginMyClub({ code });
    
    // Transform Club response to unified format
    const unifiedResponse: UnifiedLoginResponse = {
      success: response.success || false,
      token: response.token || '',
      name_en: response.data?.full_name_en || response.data?.person_name_en,
      name_ar: response.data?.full_name_ar || response.data?.person_name_ar,
      user_id: response.data?.user_id, // Include user_id from Club response data
      permissions: {
        is_saff: response.permissions?.is_saff || false,
        // Club login only provides is_saff, other permissions are undefined
        create_role: undefined,
        delete_role: undefined,
        edit_role: undefined,
        read_role: undefined,
        users_saff_read: undefined,
        users_saff_edit_role: undefined,
        create_users_outside_saff: undefined,
        read_users_outside_saff: undefined,
        edit_users_outside_saff: undefined,
        delete_users_outside_saff: undefined,
        share_delete_my_users_with_another: undefined,
        share_read_my_users_with_another: undefined,
        share_edit_my_users_with_another: undefined,
        share_create_my_users_with_another: undefined,
        can_share_with_club: undefined,
        is_supper_admin: undefined,
        is_administrators: undefined,
      },
      data: response.data
    };
    
    return unifiedResponse;
  } catch (error: any) {
    throw new Error(`Club login failed: ${error.message}`);
  }
};

/**
 * Processes the unified login response and updates the auth store only
 * @param response - The unified login response
 * @param loginType - Type of login ('saff' or 'club')
 */
export const processLoginResponse = (response: UnifiedLoginResponse, loginType: 'saff' | 'club'): void => {
  const { token, permissions, name_en, name_ar, user_id, data } = response;
  
  if (!response.success || !token) {
    throw new Error('Login failed: Invalid response');
  }

  // Set the token and permissions in auth store for both SAFF and Club users
  const setToken = useAuthStore.getState().setToken;
  const setPermissions = useAuthStore.getState().setPermissions;
  
  setToken(token);

  if (loginType === 'saff') {
    // Handle SAFF login
    const name = i18n.language === 'ar'  ? (name_ar || '' ): (name_en || '');
    setPermissions(permissions, name, user_id, false); // isClub = false for SAFF users
    
  } else if (loginType === 'club') {
    // Handle Club login - store club data in auth store using person name and user_id
    if (!data) {
      throw new Error('Club login failed: Missing club data');
    }

    // For clubs, use person_name_en as the display name and data.user_id as user_id, and set isClub = true
    const name = i18n.language === 'ar'  ? (data.full_name_ar  || data.person_name_ar || name_ar || '' ): (data.full_name_en || data.person_name_en || name_en || '');
    setPermissions(permissions, name, data.user_id, true); // isClub = true for Club users
  }
};

/**
 * Unified login handler that determines the login type and processes accordingly
 * @param code - The authentication code from URL
 * @param loginType - Type of login ('saff' or 'club')
 * @returns Promise<void>
 */
export const handleUnifiedLogin = async (code: string, loginType: 'saff' | 'club'): Promise<void> => {
  try {
    let response: UnifiedLoginResponse;

    if (loginType === 'saff') {
      response = await handleSaffLogin(code);
    } else {
      response = await handleClubLogin(code);
    }

    processLoginResponse(response, loginType);
  } catch (error: any) {
    console.error('Unified login failed:', error);
    throw error;
  }
};

/**
 * Determines login type based on URL path
 * @param pathname - The current URL pathname
 * @returns 'saff' | 'club' | null
 */
export const determineLoginType = (pathname: string): 'saff' | 'club' | null => {
  if (pathname.includes('/LoginMy')) {
    return 'saff';
  } else if (pathname.includes('/LoginClub')) {
    return 'club';
  }
  return null;
};

// Default export for backward compatibility
const UnifiedLoginService = {
  handleSaffLogin,
  handleClubLogin,
  processLoginResponse,
  handleUnifiedLogin,
  determineLoginType,
};

export default UnifiedLoginService;
